import { useTranslation } from '@dreamstack/i18n'
import { SocialMediaFacebook } from '@dreamstack/icons/src/icon-components/SocialMediaFacebook'
import { SocialMediaTwitter } from '@dreamstack/icons/src/icon-components/SocialMediaTwitter'
import { Whatsapp } from '@dreamstack/icons/src/icon-components/Whatsapp'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { trpc } from '@dreamstack/trpc/client'
import { cn } from '@dreamstack/util/src/cn'
import map from 'lodash/map'
import type { FunctionComponent } from 'react'
import { Text_13_18 } from '../layout/Texts'
import type { SocialShareProps } from './util'
import {
  SocialShareOptions,
  shareFacebook,
  shareTwitter,
  shareWhatsapp,
} from './util'

const ContentWrapper: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ className, ...rest }) => (
  <div
    className={cn(
      'relative bg-accentroWhite-full max-w-2xl w-full flex flex-col justify-between space-y-px-24',
      className
    )}
    {...rest}
  />
)
const ShareContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn(
      'mt-px-16 mb-px-16 border-t-2 border-accentroGray-200',
      className
    )}
    {...rest}
  />
)

const Divider: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn('border-t-2 border-accentroGray-200', className)}
    {...rest}
  />
)

const socials = [
  {
    name: SocialShareOptions.WHATSAPP,
    icon: <Whatsapp />,
    share: shareWhatsapp,
  },
  {
    name: SocialShareOptions.FACEBOOK,
    icon: <SocialMediaFacebook />,
    share: shareFacebook,
  },
  {
    name: SocialShareOptions.TWITTER,
    icon: <SocialMediaTwitter />,
    share: shareTwitter,
  },
]

type Social = (typeof socials)[0]

export const SocialShare: FunctionComponent<
  React.PropsWithChildren<SocialShareProps>
> = ({ title, url, text }) => {
  const t = useTranslation()
  const { mutateAsync: trackSharing } =
    trpc.anonymousTracking.trackEvent.useMutation()
  const anonymousUserId = getOrCreateAnonymousId()

  const handleOnClick = async (social: Social, url: string) => {
    social.share(url, text)

    await trackSharing({
      anonymousUserId,
      eventName: 'socialShare',
      data: { platform: social.name, url: url },
    })
  }
  return (
    <ContentWrapper>
      <div>
        <div className="text-[13px] font-semibold mb-px-8">
          {t('accentro:my.recommend')}
        </div>
        <div className="text-[20px] font-semibold">{title}</div>
      </div>
      <Divider />
      <ShareContent>
        <div className="flex justify-start flex-row space-x-px-24 w-full">
          {map(socials, (social, i) => (
            <button key={i} onClick={() => handleOnClick(social, url)}>
              <div className="flex flex-col font-semibold cursor-pointer">
                <div className="flex justify-center flex-row w-full pb-px-8">
                  {social.icon}
                </div>
                <div className="flex justify-start flex-row w-full">
                  <Text_13_18>{social.name}</Text_13_18>
                </div>
              </div>
            </button>
          ))}
        </div>
      </ShareContent>
    </ContentWrapper>
  )
}
